
import { Component, Vue, Mixins } from 'vue-property-decorator'
import CookieMethods from '@/mixins/CookieMethods.vue'
import { USER_ROLE_NAME } from '@/constants/ApiConstant'

// ログイン完了時のレスポンス
export type AuthResponse = {
  headers: {
    'access-token': string
    client: string
    uid: string
  }
  data: {
    data: {
      id: number
      role_name: string
      nickname: string
      branch_id: number
      branch_name: string
      academy_id: number
      academy_name: string
    }
  }
}

export type LoginParams = {
  branchCode?: string
  academyCode?: string
  bcbtCode: string
  password: string
  role: string
  isQrLogin?: boolean
}

/**
 * ログイン画面の共通処理をまとめたMixin用のコンポーネント
 *
 * Mixin先のログイン画面に応じてcreated時にmodeを上書きし、login関数にてログインを実行できる
 * modeに入る値は、MODEを参照
 */
@Component
export default class LoginMethods extends Mixins(CookieMethods) {
  private errorMessages: string[] = []

  private loginApiCalled = false

  /**
   * 生徒ログイン
   */
  protected async studentLogin(branchCode: string, accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      branchCode: branchCode,
      bcbtCode: accountCode,
      password: password,
      role: USER_ROLE_NAME.student,
    }
    await this.login(params)
  }

  /**
   * 生徒QRログイン
   */
  protected async studentQrLogin(bcbtCode: string, encryptedPassword: string): Promise<boolean> {
    const params: LoginParams = {
      bcbtCode: bcbtCode,
      password: encryptedPassword,
      role: USER_ROLE_NAME.student,
      isQrLogin: true,
    }
    return !!(await this.login(params))
  }

  /**
   * 教室管理者ログイン
   */
  protected async branchLogin(branchCode: string, accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      branchCode: branchCode,
      bcbtCode: accountCode,
      password: password,
      role: USER_ROLE_NAME.branch,
    }
    await this.login(params)
  }

  /**
   * 塾管理者ログイン
   */
  protected async academyLogin(academyCode: string, accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      academyCode: academyCode,
      bcbtCode: accountCode,
      password: password,
      role: USER_ROLE_NAME.academy,
    }
    await this.login(params)
  }

  /**
   * 文理管理者ログイン
   */
  protected async adminLogin(accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: accountCode,
      password: password,
      role: USER_ROLE_NAME.admin,
    }
    await this.login(params)
  }

  /**
   * 採点者ログイン
   */
  protected async markerLogin(accountCode: string, password: string): Promise<void> {
    const params: LoginParams = {
      bcbtCode: accountCode,
      password: password,
      role: USER_ROLE_NAME.marker,
    }
    await this.login(params)
  }

  /**
   * ログイン処理 (Mixin先で呼び出す)
   *
   * このリクエストで、以下の一連の処理を実行
   * 認証リクエスト-Cookieへの保存 => Topページへの遷移
   */
  protected async login(params: LoginParams): Promise<boolean | undefined> {
    // 二重呼び出し防止
    if (this.loginApiCalled) return
    this.loginApiCalled = true

    Vue.prototype.$logger.log('LoginMethods::login()', params)
    Vue.prototype.$loading.start()

    const isLoginSuccess = await Vue.prototype.$http.http
      .post('/auth/sign_in', params)
      .then(
        async (response: AuthResponse): Promise<boolean> => {
          Vue.prototype.$logger.log(response)
          Vue.prototype.$loading.complete()
          this.setCookieAuth(response)
          this.$router.push('/')
          return true
        }
      )
      .catch(
        async (error: any): Promise<boolean> => {
          Vue.prototype.$logger.log(error.response)
          if (!error.response) {
            this.errorMessages = ['ネットワークエラーが発生しています。']
          } else if (error.response.data.errors) {
            this.errorMessages = error.response.data.errors
          } else {
            this.errorMessages = ['ログイン用の認証情報が正しくありません。', '再度お試しください。']
          }
          // 失敗時は再呼び出し可能に
          this.loginApiCalled = false
          Vue.prototype.$loading.complete()
          return false
        }
      )
    return isLoginSuccess
  }

  /**
   * 認証情報をCookieに保存
   * （内部メソッドの為、外部からの呼び出しは想定していない）
   * + NOTE: https://developer.mozilla.org/ja/docs/Web/HTTP/Headers/Set-Cookie
   */
  private async setCookieAuth(res: AuthResponse): Promise<void> {
    await this.setAuthBcbt({
      accessToken: res.headers['access-token'],
      client: res.headers.client,
      uid: res.headers.uid,
      currentApiUserId: res.data.data.id,
      currentApiUserName: res.data.data.nickname,
      currentApiUserRole: res.data.data.role_name,
      currentApiBranchId: res.data.data.branch_id,
      currentApiBranchName: res.data.data.branch_name,
      currentApiAcademyId: res.data.data.academy_id,
      currentApiAcademyName: res.data.data.academy_name,
    })
  }
}
